import React, { useState, useEffect } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BarChart3, TrendingUp, Clock, Phone, DollarSign, Building2, Loader2 } from 'lucide-react';

export default function VoiceAnalytics() {
  const { fetchApi } = useCloudAuth();
  const { currentProfile, loading: profileLoading } = useBusinessProfile();
  const [loading, setLoading] = useState(true);
  const [dailyStats, setDailyStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  // Reload when business profile changes
  useEffect(() => {
    if (currentProfile?.id) {
      loadAnalytics();
    }
  }, [currentProfile?.id]);

  const loadAnalytics = async () => {
    if (!currentProfile?.id) return;
    
    try {
      setLoading(true);
      const profileParam = `businessProfileId=${currentProfile.id}`;
      const [statsRes, overviewRes] = await Promise.all([
        fetchApi(`/api/voice/stats/daily?days=14&${profileParam}`),
        fetchApi(`/api/voice/overview?${profileParam}`)
      ]);
      setDailyStats(statsRes.data || []);
      setSummary(overviewRes.data);
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Business Profile Selected</h3>
        <p className="text-gray-400">Select a business profile from the dropdown above to view analytics.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center py-8">{error}</div>
    );
  }

  const stats7d = summary?.stats7d || {};
  const stats30d = summary?.stats30d || {};

  // Calculate max for chart scaling
  const maxCalls = Math.max(...dailyStats.map(d => d.totalCalls || 0), 1);

  return (
    <div className="space-y-6" data-testid="voice-analytics">
      {/* Profile Header */}
      <div className="mb-2">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-400" />
          Analytics for {currentProfile.name}
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Call statistics and performance metrics
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Total Calls (7d)</CardTitle>
            <Phone className="h-4 w-4 text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{stats7d.totalCalls || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              {stats7d.completedCalls || 0} completed, {stats7d.failedCalls || 0} failed
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Total Minutes (7d)</CardTitle>
            <Clock className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{stats7d.totalMinutes?.toFixed(1) || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              Avg {Math.round(stats7d.avgDurationSec || 0)}s per call
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Total Cost (7d)</CardTitle>
            <DollarSign className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">${stats7d.totalCost?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-gray-500 mt-1">
              ${stats30d.totalCost?.toFixed(2) || '0.00'} this month
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {stats7d.totalCalls > 0 
                ? Math.round((stats7d.completedCalls / stats7d.totalCalls) * 100) 
                : 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Based on last 7 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Chart */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-400" />
            Daily Call Volume (Last 14 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyStats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No data available yet. Make some calls to see analytics.
            </div>
          ) : (
            <div className="space-y-3">
              {dailyStats.slice().reverse().map((day, idx) => (
                <div key={day.date || idx} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-gray-400 shrink-0">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex-1 h-6 bg-gray-800 rounded overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-500"
                      style={{ width: `${(day.totalCalls / maxCalls) * 100}%` }}
                    />
                  </div>
                  <div className="w-20 text-right text-sm">
                    <span className="text-white font-medium">{day.totalCalls}</span>
                    <span className="text-gray-500 ml-1">calls</span>
                  </div>
                  <div className="w-24 text-right text-sm text-gray-400">
                    {day.totalMinutes?.toFixed(1)} min
                  </div>
                  <div className="w-20 text-right text-sm text-green-400">
                    ${day.totalCost?.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-base">7-Day Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Total Calls</span>
              <span className="text-white">{stats7d.totalCalls || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Total Minutes</span>
              <span className="text-white">{stats7d.totalMinutes?.toFixed(2) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Avg Duration</span>
              <span className="text-white">{Math.round(stats7d.avgDurationSec || 0)}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Total Cost</span>
              <span className="text-green-400">${stats7d.totalCost?.toFixed(2) || '0.00'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-base">30-Day Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-400">Total Calls</span>
              <span className="text-white">{stats30d.totalCalls || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Total Minutes</span>
              <span className="text-white">{stats30d.totalMinutes?.toFixed(2) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Avg Duration</span>
              <span className="text-white">{Math.round(stats30d.avgDurationSec || 0)}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Total Cost</span>
              <span className="text-green-400">${stats30d.totalCost?.toFixed(2) || '0.00'}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
